/**
 * YearDayCell Component
 *
 * Year view cell showing date number for a day.
 * Composes the DayCell atom with date display.
 *
 * @example
 * ```tsx
 * <YearDayCell
 *   date={new Date()}
 *   status="fullyBooked"
 *   isToday
 *   onClick={handleClick}
 * />
 * ```
 */

import { DayCell } from "@/components/schedule/DayCell";
import classNames from "classnames";
import React from "react";
import styles from "./YearDayCell.module.css";
import type { YearDayCellProps } from "./types";

export const YearDayCell: React.FC<YearDayCellProps> = ({
  date,
  hours,
  status = "free",
  colored = true,
  isToday = false,
  isWeekend = false,
  selected = false,
  selectable = false,
  inRangePreview = false,
  absenceFill = 0,
  disabled = false,
  onClick,
  className,
  "data-testid": testId,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...rest
}) => {
  const dayNumber = date.getDate();

  return (
    <div
      className={classNames(styles.container, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      {...rest}
    >
      <span className={classNames(styles.date, isToday && styles.today)}>
        {dayNumber}
      </span>

      <DayCell
        status={status}
        size="sm"
        colored={colored}
        isWeekend={isWeekend}
        isToday={isToday}
        selected={selected}
        selectable={selectable}
        inRangePreview={inRangePreview}
        absenceFill={absenceFill}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        className={styles.dayCell}
      >
        <span className={styles.hours}>{hours > 0 ? hours : ""}</span>
      </DayCell>
    </div>
  );
};

YearDayCell.displayName = "YearDayCell";

export default YearDayCell;
